/**
 * BetriebsvereinbarungenPage.jsx
 * Admin-Seite für Betriebsvereinbarungen
 * Tab unter Personal
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad from '../../components/ui/SignaturePad';
import {
  Plus, Send, Archive, Edit2, CheckCircle2, Clock, AlertCircle, CheckCircle,
  Users, FileText, Download, ChevronDown, ChevronUp, Loader2,
  Sparkles, Image as ImageIcon, X, RefreshCw, Shield, Info,
  ToggleLeft, ToggleRight, Trash2, PenLine,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MarkdownField } from '../../components/MarkdownField';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Ausstehend', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300', icon: Clock },
    employee_signed: { label: 'MA unterschrieben', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300', icon: CheckCircle2 },
    completed: { label: 'Vollständig', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300', icon: CheckCircle2 },
    outdated: { label: 'Veraltet', color: 'bg-gray-100 dark:bg-gray-800 text-gray-500', icon: Archive },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
      <Icon className="h-3 w-3" />{s.label}
    </span>
  );
}

// ─── BV Erstellen / Bearbeiten Modal ─────────────────────────────────────────
function BVModal({ bv, locations, onClose, onSaved }) {
  const [title, setTitle] = useState(bv?.title || '');
  const [content, setContent] = useState(bv?.content || '');
  const [image, setImage] = useState(bv?.image_base64 || null);
  const [isUrgent, setIsUrgent] = useState(bv?.is_urgent || false);
  const [autoSend, setAutoSend] = useState(bv?.auto_send_to_new !== false);
  const [confirmText, setConfirmText] = useState(bv?.confirmation_text || '');
  const [isMajorChange, setIsMajorChange] = useState(false);
  const [selectedLocs, setSelectedLocs] = useState(bv?.location_ids || []); // [] = alle Standorte
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [showAi, setShowAi] = useState(false);
  const isEdit = !!bv;

  const handleAiGenerate = async () => {
    if (!aiDesc.trim()) { toast.error('Bitte beschreibe die Vereinbarung'); return; }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/api/betriebsvereinbarungen/generate-text`,
        { beschreibung: aiDesc }, { headers: authHeader() });
      setContent(res.data.text || '');
      setShowAi(false);
      toast.success('KI hat Text generiert – bitte prüfen und anpassen');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KI-Fehler');
    } finally { setAiLoading(false); }
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Titel ist Pflicht'); return; }
    if (!content.trim()) { toast.error('Inhalt ist Pflicht'); return; }
    setSaving(true);
    try {
      const payload = {
        title, content, image_base64: image || null,
        is_urgent: isUrgent, auto_send_to_new: autoSend,
        confirmation_text: confirmText || undefined,
        location_ids: selectedLocs.length > 0 ? selectedLocs : [],
        ...(isEdit ? { is_major_change: isMajorChange } : {}),
      };
      let res;
      if (isEdit) {
        res = await axios.put(`${API}/api/betriebsvereinbarungen/${bv.id}`, payload, { headers: authHeader() });
      } else {
        res = await axios.post(`${API}/api/betriebsvereinbarungen`, payload, { headers: authHeader() });
      }
      toast.success(isEdit ? 'Vereinbarung aktualisiert' : 'Vereinbarung erstellt');
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[95vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-base font-bold">{isEdit ? 'Vereinbarung bearbeiten' : 'Neue Vereinbarung'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Titel */}
          <div className="space-y-1">
            <Label className="text-xs">Titel *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Kassierpflichten Tankstelle" data-testid="bv-title-input" />
          </div>

          {/* KI-Assistent */}
          <div className="border border-blue-200 dark:border-blue-700 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowAi(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
              <Sparkles className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">KI schreibt Vereinbarungstext</span>
              <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">(~0,1 Cent)</span>
              {showAi ? <ChevronUp className="h-4 w-4 text-blue-400 ml-auto" /> : <ChevronDown className="h-4 w-4 text-blue-400 ml-auto" />}
            </button>
            {showAi && (
              <div className="px-4 pb-4 pt-3 space-y-3">
                <textarea
                  value={aiDesc}
                  onChange={e => setAiDesc(e.target.value)}
                  placeholder="Beschreibe kurz was die Vereinbarung regeln soll... z.B. 'Kassierpflichten: Mitarbeiter muss alle Kassiervorgänge ordentlich abschließen. Bei Verstoß folgt Abmahnung'"
                  className="w-full h-24 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  data-testid="ai-description-input"
                />
                <Button onClick={handleAiGenerate} disabled={aiLoading} size="sm" className="w-full">
                  {aiLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />KI generiert...</> : <><Sparkles className="h-4 w-4 mr-2" />Text generieren</>}
                </Button>
              </div>
            )}
          </div>

          {/* Inhalt */}
          <div className="space-y-1">
            <Label className="text-xs">Inhalt *</Label>
            <MarkdownField
              value={content}
              onChange={setContent}
              placeholder="Vereinbarungstext... (Fett **…**, Kursiv *…*, Aufzählung mit - )"
              testId="bv-content-input"
            />
          </div>

          {/* Bild */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Bild (optional)</Label>
            {image ? (
              <div className="relative">
                <img src={image} alt="Vereinbarung Bild" className="rounded-xl border max-h-32 object-contain w-full bg-muted" />
                <button onClick={() => setImage(null)} className="absolute top-2 right-2 p-1.5 bg-white dark:bg-gray-800 border rounded-full hover:bg-red-50 text-red-500">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 h-16 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all" data-testid="bv-image-upload">
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Bild hochladen</span>
              </label>
            )}
          </div>

          {/* Standort-Zuordnung */}
          {locations.length > 1 && (
            <div className="space-y-2">
              <Label className="text-xs">Gilt für Standorte</Label>
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={selectedLocs.length === 0}
                    onChange={() => setSelectedLocs([])} className="h-4 w-4 rounded" />
                  <span className="text-sm font-medium">Alle Standorte</span>
                </label>
                {locations.map(loc => (
                  <label key={loc.id} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox"
                      checked={selectedLocs.includes(loc.id)}
                      onChange={e => {
                        if (e.target.checked) setSelectedLocs(p => [...p, loc.id]);
                        else setSelectedLocs(p => p.filter(x => x !== loc.id));
                      }}
                      className="h-4 w-4 rounded" data-testid={`loc-checkbox-${loc.id}`} />
                    <span className="text-sm">{loc.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedLocs.length === 0 ? 'Alle Mitarbeiter erhalten diese Vereinbarung.' : `Nur Mitarbeiter an ${selectedLocs.length} Standort(en).`}
              </p>
            </div>
          )}

          {/* Bestätigungstext */}
          <div className="space-y-1">
            <Label className="text-xs">Bestätigungstext (optional, sonst Standard)</Label>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)}
              placeholder="Ich habe die Betriebsvereinbarung gelesen und verstanden." />
          </div>

          {/* Optionen */}
          <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isUrgent} onChange={e => setIsUrgent(e.target.checked)} className="h-4 w-4 rounded" />
              <div>
                <p className="text-sm font-medium">Dringend</p>
                <p className="text-xs text-muted-foreground">Mitarbeiter bekommt sofort Push, nicht erst nach 3 Tagen</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={autoSend} onChange={e => setAutoSend(e.target.checked)} className="h-4 w-4 rounded" />
              <div>
                <p className="text-sm font-medium">Neue Mitarbeiter erhalten diese Vereinbarung automatisch</p>
              </div>
            </label>
            {isEdit && (
              <label className="flex items-center gap-3 cursor-pointer border-t border-gray-200 dark:border-gray-700 pt-3 mt-1">
                <input type="checkbox" checked={isMajorChange} onChange={e => setIsMajorChange(e.target.checked)} className="h-4 w-4 rounded" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Wichtige Änderung → alle müssen erneut unterschreiben</p>
                  <p className="text-xs text-muted-foreground">Erstellt neue Version. Kleine Korrekturen: Haken weglassen.</p>
                </div>
              </label>
            )}
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="bv-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            {isEdit ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── SL-Berechtigung Modal ────────────────────────────────────────────────────
function SLPermissionModal({ onClose }) {
  const [sls, setSls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/betriebsvereinbarungen/sl-permissions`, { headers: authHeader() })
      .then(r => setSls(r.data || []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (userId, current) => {
    setSaving(userId);
    try {
      await axios.patch(`${API}/api/betriebsvereinbarungen/sl-permission/${userId}`,
        { enabled: !current }, { headers: authHeader() });
      setSls(prev => prev.map(sl => sl.id === userId ? { ...sl, bv_permission: !current } : sl));
      toast.success('Berechtigung aktualisiert');
    } catch (e) {
      toast.error('Fehler');
    } finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-base font-bold">SL-Berechtigung: Betriebsvereinbarungen</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Welche Stationsleitungen dürfen BVen verwalten?</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : sls.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Keine Stationsleitungen vorhanden</p>
          ) : sls.map(sl => (
            <div key={sl.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
              <div>
                <p className="text-sm font-semibold">{sl.name}</p>
                <p className="text-xs text-muted-foreground">{sl.email}</p>
              </div>
              <button
                onClick={() => toggle(sl.id, sl.bv_permission)}
                disabled={saving === sl.id}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${sl.bv_permission ? 'bg-primary/10 text-primary' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}
                data-testid={`sl-permission-toggle-${sl.id}`}
              >
                {saving === sl.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                  sl.bv_permission ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                {sl.bv_permission ? 'Aktiviert' : 'Deaktiviert'}
              </button>
            </div>
          ))}
        </div>
        <div className="px-5 pb-4">
          <Button variant="outline" onClick={onClose} className="w-full">Schließen</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Batch Send Modal ─────────────────────────────────────────────────────────
function BatchSendModal({ bvs, employees, locations, onClose, onSent }) {
  const [selectedBVs, setSelectedBVs] = useState(bvs.filter(b => b.status === 'active').map(b => b.id));
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [filterByLoc, setFilterByLoc] = useState(false);
  const [selectedLocs, setSelectedLocs] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleBV = (id) => setSelectedBVs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleEmp = (id) => setSelectedEmps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSend = async () => {
    if (selectedBVs.length === 0) { toast.error('Bitte mindestens eine Vereinbarung auswählen'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/betriebsvereinbarungen/batch-send`, {
        bv_ids: selectedBVs,
        employee_ids: sendToAll ? null : selectedEmps.length > 0 ? selectedEmps : null,
        location_ids: filterByLoc && selectedLocs.length > 0 ? selectedLocs : null,
      }, { headers: authHeader() });
      toast.success(`${res.data.created} Zuweisungen erstellt und versendet!`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-base font-bold">Vereinbarungen versenden</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          <div className="space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Vereinbarungen auswählen</p>
            {bvs.filter(b => b.status === 'active').map(bv => (
              <label key={bv.id} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <input type="checkbox" checked={selectedBVs.includes(bv.id)} onChange={() => toggleBV(bv.id)} className="h-4 w-4 rounded" data-testid={`batch-bv-${bv.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{bv.title}</p>
                  {bv.is_urgent && <span className="text-xs text-red-600 font-medium">Dringend</span>}
                </div>
              </label>
            ))}
          </div>

          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">An wen senden?</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="radio" checked={sendToAll} onChange={() => setSendToAll(true)} className="h-4 w-4" />
              <div><p className="text-sm font-semibold">Alle Mitarbeiter</p><p className="text-xs text-muted-foreground">({employees.length} Mitarbeiter)</p></div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="radio" checked={!sendToAll} onChange={() => setSendToAll(false)} className="h-4 w-4" />
              <p className="text-sm font-semibold">Auswählen</p>
            </label>
            {!sendToAll && (
              <div className="space-y-1 pl-7">
                {employees.map(emp => (
                  <label key={emp.id} className="flex items-center gap-2 cursor-pointer py-1">
                    <input type="checkbox" checked={selectedEmps.includes(emp.id)} onChange={() => toggleEmp(emp.id)} className="h-4 w-4 rounded" />
                    <span className="text-sm">{emp.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {locations.length > 1 && (
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="radio" checked={filterByLoc} onChange={() => { setFilterByLoc(true); setSendToAll(false); }} className="h-4 w-4" />
                <p className="text-sm font-semibold">Nach Standort filtern</p>
              </label>
              {filterByLoc && (
                <div className="flex flex-wrap gap-2 pl-7">
                  {locations.map(loc => (
                    <label key={loc.id} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox"
                        checked={selectedLocs.includes(loc.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedLocs(p => [...p, loc.id]);
                          else setSelectedLocs(p => p.filter(x => x !== loc.id));
                        }}
                        className="h-4 w-4 rounded" />
                      <span className="text-sm">{loc.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-xl">
            <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-400">
              Mitarbeiter erhalten eine Push-Benachrichtigung. Bereits zugewiesene Vereinbarungen werden nicht doppelt gesendet.
            </p>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={sending}>Abbrechen</Button>
          <Button onClick={handleSend} disabled={sending || selectedBVs.length === 0} className="flex-1" data-testid="batch-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Versenden
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Export Modal (redesigned) ──────────────────────────────────────────────
function ExportModal({ bvs, locations, onClose }) {
  // Multi-BV-Auswahl: alle aktiven + archivierten BVs
  const allBvs = bvs; // Parent übergibt ALLE BVs
  const [selectedBvIds, setSelectedBvIds] = useState([]); // leer = alle
  const [filterLoc, setFilterLoc] = useState('');
  const [includeSignatures, setIncludeSignatures] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [format, setFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);

  const toggleBvId = (id) => setSelectedBvIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleExport = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    if (selectedBvIds.length > 0) params.set('bv_ids', selectedBvIds.join(','));
    if (filterLoc) params.set('location_id', filterLoc);
    if (format === 'pdf') {
      params.set('include_signatures', includeSignatures.toString());
      params.set('include_archived', includeArchived.toString());
    }

    const endpoint = format === 'csv'
      ? `/api/betriebsvereinbarungen/export/csv`
      : `/api/betriebsvereinbarungen/export/pdf-report`;

    setExporting(true);
    try {
      const url = `${API_URL}${endpoint}?${params.toString()}`;
      const resp = await fetch(url, { headers: authHeader() });
      if (!resp.ok) throw new Error('Export fehlgeschlagen');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = format === 'csv' ? 'BV_Export.csv' : 'BV_Bericht.pdf';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 3000);
      onClose();
    } catch { toast.error('Export fehlgeschlagen'); }
    finally { setExporting(false); }
  };

  const activeBvs = allBvs.filter(b => b.status === 'active');
  const archivedBvs = allBvs.filter(b => b.status === 'archived');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-base font-bold">Export</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Format */}
          <div className="space-y-2">
            <Label className="text-xs">Format</Label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="pdf" checked={format === 'pdf'} onChange={() => setFormat('pdf')} className="h-4 w-4" />
                <span className="text-sm font-medium">PDF-Bericht</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="csv" checked={format === 'csv'} onChange={() => setFormat('csv')} className="h-4 w-4" />
                <span className="text-sm font-medium">CSV (Excel)</span>
              </label>
            </div>
          </div>

          {/* BV-Auswahl (Checkboxen, alle zeigen) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Vereinbarungen</Label>
              <button className="text-xs text-primary hover:underline"
                onClick={() => setSelectedBvIds(selectedBvIds.length === 0 ? allBvs.map(b => b.id) : [])}>
                {selectedBvIds.length === 0 ? 'Alle auswählen' : 'Alle abwählen'}
              </button>
            </div>
            <div className="space-y-1 max-h-44 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl p-2">
              {activeBvs.length > 0 && <p className="text-[10px] text-muted-foreground uppercase tracking-wide px-1 pt-1">Aktiv</p>}
              {activeBvs.map(bv => (
                <label key={bv.id} className="flex items-center gap-2 p-1.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                  <input type="checkbox" checked={selectedBvIds.includes(bv.id)} onChange={() => toggleBvId(bv.id)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{bv.title}</span>
                </label>
              ))}
              {archivedBvs.length > 0 && <p className="text-[10px] text-muted-foreground uppercase tracking-wide px-1 pt-2">Archiviert</p>}
              {archivedBvs.map(bv => (
                <label key={bv.id} className="flex items-center gap-2 p-1.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 opacity-60">
                  <input type="checkbox" checked={selectedBvIds.includes(bv.id)} onChange={() => toggleBvId(bv.id)} className="h-4 w-4 rounded" />
                  <span className="text-sm">{bv.title}</span>
                </label>
              ))}
              {allBvs.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Keine BVen vorhanden</p>}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {selectedBvIds.length === 0 ? 'Alle BVen exportieren' : `${selectedBvIds.length} BV(en) ausgewählt`}
            </p>
          </div>

          {/* Standort-Filter */}
          <div className="space-y-1">
            <Label className="text-xs">Standort (optional)</Label>
            <select value={filterLoc} onChange={e => setFilterLoc(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 focus:outline-none">
              <option value="">Alle Standorte</option>
              {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
            </select>
          </div>

          {/* PDF-spezifische Optionen */}
          {format === 'pdf' && (
            <div className="space-y-2">
              <Label className="text-xs">PDF-Optionen</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeSignatures} onChange={e => setIncludeSignatures(e.target.checked)} className="h-4 w-4 rounded" />
                <span className="text-sm">Unterschriften-Bilder einbetten</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} className="h-4 w-4 rounded" />
                <span className="text-sm">Archivierte Mitarbeiter einschließen</span>
              </label>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1">Abbrechen</Button>
          <Button onClick={handleExport} disabled={exporting} className="flex-1 gap-1.5" data-testid="export-download-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {format === 'csv' ? 'CSV herunterladen' : 'PDF herunterladen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
function AssignmentsModal({ bv, onClose, onCompanySign }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompanySign, setShowCompanySign] = useState(false);
  const [signingCompany, setSigningCompany] = useState(false);
  const [signingId, setSigningId] = useState(null); // Backward-compat: einzelner MA-Admin-Sign
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);
  const companySigRef = useRef(null);
  const [sigError, setSigError] = useState(false);

  const reload = () => {
    axios.get(`${API}/api/betriebsvereinbarungen/assignments/overview?bv_id=${bv.id}`, { headers: authHeader() })
      .then(r => setAssignments(r.data || []))
      .catch(() => toast.error('Fehler'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [bv.id]); // eslint-disable-line

  // Unternehmens-Unterschrift speichern
  const handleCompanySign = async () => {
    const sig = companySigRef.current?.getCanvas().toDataURL('image/png');
    if (!sig || companySigRef.current?.isEmpty()) { toast.error('Bitte unterschreiben'); return; }
    setSigningCompany(true);
    try {
      await axios.post(`${API}/api/betriebsvereinbarungen/${bv.id}/company-sign`,
        { signature: sig }, { headers: authHeader() });
      toast.success(`Unternehmens-Unterschrift gespeichert! Alle wartenden Mitarbeiter-Assignments wurden automatisch abgeschlossen.`);
      setShowCompanySign(false);
      reload();
      onCompanySign && onCompanySign(bv.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
    finally { setSigningCompany(false); }
  };

  // Backward-compat: Einzelner Admin-Sign (falls jemand vor dem Update unterschrieben hat)
  const handleAdminSign = async () => {
    const sig = sigRef.current?.getCanvas().toDataURL('image/png');
    if (!sig || sigRef.current?.isEmpty()) { setSigError(true); toast.error('Bitte unterschreiben'); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/api/betriebsvereinbarungen/assignments/${signingId}/admin-sign`,
        { signature: sig }, { headers: authHeader() });
      toast.success('Unterschrift gespeichert');
      setSigningId(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
    finally { setSigning(false); }
  };

  const pending = assignments.filter(a => a.status === 'pending').length;
  const needsAdminSign = assignments.filter(a => a.status === 'employee_signed').length;
  const signed = assignments.filter(a => a.status === 'completed').length;
  const hasCompanySig = !!bv.company_signature;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold truncate">{bv.title}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {signed} vollständig · {needsAdminSign > 0 && !hasCompanySig && <span className="text-amber-600 font-medium">{needsAdminSign} warten auf AG-Unterschrift · </span>}{pending} ausstehend
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>

        {/* ── Unternehmens-Unterschrift Block ── */}
        <div className={`px-5 py-3 border-b flex-shrink-0 ${hasCompanySig ? 'bg-green-50 dark:bg-green-900/10 border-green-200' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {hasCompanySig
                ? <><CheckCircle className="h-4 w-4 text-green-600 shrink-0" /><span className="text-sm font-medium text-green-800 dark:text-green-200">Unternehmens-Unterschrift: {bv.company_signed_by} · {bv.company_signed_at?.slice(0,10)}</span></>
                : <><AlertCircle className="h-4 w-4 text-amber-600 shrink-0" /><span className="text-sm font-medium text-amber-800 dark:text-amber-200">Noch keine Unternehmens-Unterschrift</span></>
              }
            </div>
            <Button size="sm" variant="outline"
              className={hasCompanySig ? 'border-green-400 text-green-700' : 'border-amber-400 text-amber-700'}
              onClick={() => setShowCompanySign(v => !v)}
              data-testid="company-sign-toggle-btn">
              <PenLine className="h-3.5 w-3.5 mr-1" />
              {hasCompanySig ? 'Neu unterzeichnen' : 'Jetzt unterzeichnen'}
            </Button>
          </div>
          {showCompanySign && (
            <div className="mt-3 space-y-2">
              <div className="rounded-xl overflow-hidden border-2 border-amber-300">
                <SignaturePad ref={companySigRef} height={130} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => { setShowCompanySign(false); companySigRef.current?.clear(); }}>Abbrechen</Button>
                <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={handleCompanySign} disabled={signingCompany} data-testid="company-sign-save-btn">
                  {signingCompany ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}
                  Unterzeichnen
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Einzelner Admin-Sign Pad (backward-compat) */}
        {signingId && (
          <div className="px-5 py-4 border-b border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 space-y-3 flex-shrink-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <PenLine className="h-4 w-4" />
              Einzelne Admin-Unterschrift: <span className="font-bold">{assignments.find(a => a.id === signingId)?.employee_name}</span>
            </p>
            <div className={`rounded-xl overflow-hidden border-2 ${sigError ? 'border-red-400' : 'border-amber-200'}`}>
              <SignaturePad ref={sigRef} height={130} onBegin={() => setSigError(false)} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSigningId(null); setSigError(false); }} disabled={signing}>Abbrechen</Button>
              <Button size="sm" className="flex-1" onClick={handleAdminSign} disabled={signing} data-testid="admin-sign-confirm-btn">
                {signing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}
                Unterschrift speichern
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 divide-y divide-gray-100 dark:divide-gray-800">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">Noch nicht versendet</p>
          ) : assignments.map(a => (
            <div key={a.id} className="flex items-center gap-3 px-5 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{a.employee_name}</p>
                <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-0.5">
                  {a.employee_confirmed_read && <span>Gelesen: {a.employee_read_at?.slice(0,10)}</span>}
                  {a.employee_signed_at && <span>MA: {a.employee_signed_at?.slice(0,10)}</span>}
                  {a.admin_signed_at && <span className="text-green-600">Admin: {a.admin_signed_at?.slice(0,10)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <StatusBadge status={a.status} />
                {/* Admin unterschreiben wenn MA bereits unterschrieben */}
                {a.status === 'employee_signed' && (
                  <button
                    onClick={() => { setSigningId(a.id); setSigError(false); setTimeout(() => sigRef.current?.clear(), 100); }}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 text-amber-700 dark:text-amber-300 text-xs font-medium transition-colors"
                    title="Als Admin unterschreiben"
                    data-testid={`admin-sign-btn-${a.id}`}
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Unterschreiben
                  </button>
                )}
                {a.status === 'completed' && (
                  <button
                    onClick={() => window.open(`${API}/api/betriebsvereinbarungen/assignments/${a.id}/pdf`, '_blank')}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                    title="PDF herunterladen"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <Button variant="outline" onClick={onClose} className="w-full">Schließen</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Hauptseite ───────────────────────────────────────────────────────────────
// Markdown aus Vorschautext entfernen (** → plain)
function stripMd(text) {
  return (text || '').replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1').replace(/#{1,4}\s*/g, '').replace(/^[-•]\s/gm, '');
}

export function BetriebsvereinbarungenPage({ employees = [], userRole }) {
  const { user } = useAuth();
  const [bvs, setBvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editBV, setEditBV] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSLModal, setShowSLModal] = useState(false);
  const [viewBV, setViewBV] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const role = userRole || user?.role || 'employee';
  const isAdmin = role === 'admin' || role === 'superadmin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/betriebsvereinbarungen?include_archived=${showArchived}`, { headers: authHeader() });
      setBvs(res.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  // Locations für Filter laden
  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authHeader() })
      .then(r => setLocations(r.data || []))
      .catch(() => {});
  }, []);

  const handleArchive = async (bv) => {
    if (!window.confirm(`"${bv.title}" archivieren?`)) return;
    try {
      await axios.patch(`${API}/api/betriebsvereinbarungen/${bv.id}/archive`, {}, { headers: authHeader() });
      toast.success('Archiviert');
      load();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  const activeBVs = bvs.filter(b => b.status === 'active');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold">Betriebsvereinbarungen</h2>
          <p className="text-sm text-muted-foreground">Erstellen, versenden und verfolgen</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowSLModal(true)} className="gap-1.5" data-testid="sl-permissions-btn">
              <Shield className="h-4 w-4" /> SL-Berechtigung
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowExportModal(true)} className="gap-1.5" data-testid="export-open-btn">
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBatchModal(true)} disabled={activeBVs.length === 0} className="gap-1.5" data-testid="batch-send-open-btn">
            <Send className="h-4 w-4" /> Versenden
          </Button>
          <Button size="sm" onClick={() => setShowCreateModal(true)} className="gap-1.5" data-testid="create-bv-btn">
            <Plus className="h-4 w-4" /> Neue Vereinbarung
          </Button>
        </div>
      </div>

      {/* Toggle Archiv */}
      <label className="flex items-center gap-2 cursor-pointer w-fit">
        <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="h-4 w-4 rounded" />
        <span className="text-sm text-muted-foreground">Archivierte anzeigen</span>
      </label>

      {/* BV Liste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : bvs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">Noch keine Vereinbarungen</p>
          <p className="text-sm mt-1">Erstelle deine erste Betriebsvereinbarung</p>
          <Button className="mt-4 gap-1.5" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4" /> Jetzt erstellen
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {bvs.map(bv => (
            <div key={bv.id} className={`bg-white dark:bg-gray-900 border rounded-xl p-4 space-y-3 ${bv.status === 'archived' ? 'opacity-60 border-gray-200 dark:border-gray-700' : 'border-gray-200 dark:border-gray-700'}`}
              data-testid={`bv-card-${bv.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">{bv.title}</h3>
                    {bv.is_urgent && <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded-full font-medium">Dringend</span>}
                    {bv.status === 'archived' && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Archiviert</span>}
                    {bv.version > 1 && <span className="text-xs text-gray-400 font-mono">v{bv.version}</span>}
                    {bv.location_ids?.length > 0 && (
                      <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
                        {bv.location_ids.length} Standort(e)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{stripMd(bv.content)?.slice(0, 120)}…</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => setViewBV(bv)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" title="Wer hat unterschrieben" data-testid={`view-assignments-${bv.id}`}>
                    <Users className="h-4 w-4" />
                  </button>
                  {bv.status === 'active' && <>
                    <button onClick={() => setEditBV(bv)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" title="Bearbeiten" data-testid={`edit-bv-${bv.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleArchive(bv)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500" title="Archivieren">
                      <Archive className="h-4 w-4" />
                    </button>
                  </>}
                </div>
              </div>

              {/* Statistik */}
              {bv.total_assigned > 0 && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3.5 w-3.5" />{bv.total_signed} unterschrieben</span>
                  {bv.pending_count > 0 && <span className="flex items-center gap-1 text-amber-600"><Clock className="h-3.5 w-3.5" />{bv.pending_count} ausstehend</span>}
                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${bv.total_assigned > 0 ? (bv.total_signed / bv.total_assigned) * 100 : 0}%` }} />
                  </div>
                </div>
              )}
              {bv.total_assigned === 0 && bv.status === 'active' && (
                <p className="text-xs text-amber-600">Noch nicht versendet</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <BVModal locations={locations} onClose={() => setShowCreateModal(false)} onSaved={(bv) => { load(); setShowCreateModal(false); }} />
      )}
      {editBV && (
        <BVModal bv={editBV} locations={locations} onClose={() => setEditBV(null)} onSaved={() => { load(); setEditBV(null); }} />
      )}
      {showBatchModal && (
        <BatchSendModal bvs={activeBVs} employees={employees} locations={locations} onClose={() => setShowBatchModal(false)} onSent={() => { load(); setShowBatchModal(false); }} />
      )}
      {showSLModal && <SLPermissionModal onClose={() => setShowSLModal(false)} />}
      {viewBV && <AssignmentsModal bv={viewBV} onClose={() => setViewBV(null)}
        onCompanySign={() => { load(); setViewBV(v => v ? ({ ...v, company_signature: 'signed' }) : null); }} />}
      {showExportModal && <ExportModal bvs={bvs} locations={locations} onClose={() => setShowExportModal(false)} />}
    </div>
  );
}
